import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import { RealtimeClient } from "./client";
import type { RealtimeEvent, RealtimeStatus, TokenProvider } from "./types";
import { parseRealtimeFrame, VersionGate } from "./versionGate";
import type { EntityRef, RealtimeFrame } from "./versionGate";

export interface UseRealtimeInvalidationOptions {
  /** SSE endpoint; defaults to the API origin's realtime endpoint. */
  url?: string;
  getToken: TokenProvider;
  /** Gate the subscription on auth state; the client stays closed while false. */
  enabled?: boolean;
  /**
   * Maps an event to the react-query key(s) to invalidate. Returning `undefined` invalidates
   * every query — the safe default, since events are rare invalidation hints (ADR-0012).
   */
  resolveQueryKey?: (event: RealtimeEvent) => QueryKey | undefined;
  /**
   * Per-record signal (§D.3, ADR-0055): fired for each AGGREGATE frame that passes the version gate
   * (newest-per-`EntityRef`; stale/duplicate frames are dropped and never fire this). Carries the
   * `actorStaffId` the UI composes attribution from. Not fired for presence/control frames.
   */
  onRecordChanged?: (frame: RealtimeFrame) => void;
  /** Fired when a `presence.changed` frame arrives for an entity — drives `usePresence` refetch. */
  onPresenceChanged?: (entity: EntityRef) => void;
}

function defaultUrl(): string {
  const base: string = import.meta.env?.VITE_API_URL ?? "";
  return `${base}/api/v1/realtime/events`;
}

/** Parse the `{ entity: { type, id } }` payload of a `presence.changed` frame. */
function parsePresenceEntity(event: RealtimeEvent): EntityRef | null {
  try {
    const p = JSON.parse(event.data) as { entity?: { type?: unknown; id?: unknown } };
    const e = p.entity;
    if (e && typeof e.type === "string" && typeof e.id === "string")
      return { type: e.type, id: e.id };
  } catch {
    return null;
  }
  return null;
}

/**
 * Subscribes to the venue's live event stream and turns each event into a react-query invalidation —
 * components refetch through their normal query path; the event is never the source of truth.
 *
 * Ordered delivery (ADR-0055): aggregate frames pass a per-`EntityRef` {@link VersionGate}; a frame not
 * newer than the last applied for that record is DROPPED (no invalidate, no signal) so stale data is
 * never shown, while a forward gap still refetches. `presence.changed` / `connected` control frames are
 * routed to their own signals, not the version gate. On (re)connect the gate resets — the client
 * snapshots via a fresh invalidate, it never replays. Returns the connection status for UI display.
 */
export function useRealtimeInvalidation(options: UseRealtimeInvalidationOptions): RealtimeStatus {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>("closed");
  // Written from an effect (not during render) — refs must not be written during render.
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (!enabled) return;
    const gate = new VersionGate();
    const client = new RealtimeClient({
      url: optionsRef.current.url ?? defaultUrl(),
      getToken: (...args) => optionsRef.current.getToken(...args),
      onStatusChange: setStatus,
      onEvent: (event) => {
        const opts = optionsRef.current;

        // Control frames: reset the gate on (re)connect (snapshot, never replay); route presence.
        if (event.type === "connected") {
          gate.reset();
          return;
        }
        if (event.type === "presence.changed") {
          const entity = parsePresenceEntity(event);
          if (entity) opts.onPresenceChanged?.(entity);
          return;
        }

        // Aggregate frames: drop stale versions before doing anything (ordered delivery).
        const frame = parseRealtimeFrame(event);
        if (frame) {
          if (gate.admit(frame.entity, frame.version) === "drop") return;
          opts.onRecordChanged?.(frame);
        }

        const key = opts.resolveQueryKey?.(event);
        void queryClient.invalidateQueries(key ? { queryKey: key } : undefined);
      },
    });
    client.start();
    return () => client.stop();
  }, [enabled, queryClient]);

  return status;
}
